import { useState } from 'react';
import type { CaptureForm, FormField, FieldType } from '../../../mocks/leadCapture';

interface Props {
  form: CaptureForm | null;
  onClose: () => void;
  onSave: (form: CaptureForm) => void;
}

const fieldTypes: { type: FieldType; label: string; icon: string }[] = [
  { type: 'text', label: 'Short Text', icon: 'ri-text' },
  { type: 'email', label: 'Email', icon: 'ri-mail-line' },
  { type: 'phone', label: 'Phone', icon: 'ri-phone-line' },
  { type: 'number', label: 'Number', icon: 'ri-hashtag' },
  { type: 'select', label: 'Dropdown', icon: 'ri-list-check' },
  { type: 'textarea', label: 'Long Text', icon: 'ri-article-line' },
  { type: 'date', label: 'Date', icon: 'ri-calendar-line' },
  { type: 'checkbox', label: 'Checkbox', icon: 'ri-checkbox-line' },
];

const categoryOptions = ['General', 'Visa', 'Premium', 'Corporate', 'Group'];
const colorOptions = ['#0ea5e9', '#10b981', '#f43f5e', '#f59e0b', '#8b5cf6', '#6366f1', '#14b8a6', '#ec4899'];

const defaultForm: CaptureForm = {
  id: `form-${Date.now()}`,
  name: '',
  description: '',
  status: 'Draft',
  icon: 'ri-suitcase-line',
  color: '#0ea5e9',
  category: 'General',
  submissions: 0,
  converted: 0,
  views: 0,
  createdAt: new Date().toISOString().split('T')[0],
  updatedAt: new Date().toISOString().split('T')[0],
  fields: [],
};

const iconOptions = [
  'ri-suitcase-line', 'ri-passport-line', 'ri-heart-line', 'ri-briefcase-line',
  'ri-group-line', 'ri-phone-line', 'ri-mail-line', 'ri-map-2-line',
];

export default function FormBuilder({ form, onClose, onSave }: Props) {
  const [data, setData] = useState<CaptureForm>(form ? { ...form, fields: form.fields.map((f) => ({ ...f })) } : { ...defaultForm, id: `form-${Date.now()}` });
  const [editingFieldId, setEditingFieldId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'fields' | 'settings' | 'preview'>('fields');
  const [newOption, setNewOption] = useState('');

  const addField = (type: FieldType) => {
    const newField: FormField = {
      id: `f${Date.now()}`,
      type,
      label: fieldTypes.find((f) => f.type === type)?.label || type,
      placeholder: '',
      required: false,
      options: type === 'select' ? ['Option 1', 'Option 2'] : undefined,
    };
    setData((d) => ({ ...d, fields: [...d.fields, newField] }));
    setEditingFieldId(newField.id);
  };

  const removeField = (id: string) => {
    setData((d) => ({ ...d, fields: d.fields.filter((f) => f.id !== id) }));
    if (editingFieldId === id) setEditingFieldId(null);
  };

  const updateField = (id: string, updates: Partial<FormField>) => {
    setData((d) => ({
      ...d,
      fields: d.fields.map((f) => (f.id === id ? { ...f, ...updates } : f)),
    }));
  };

  const moveField = (id: string, dir: 'up' | 'down') => {
    const idx = data.fields.findIndex((f) => f.id === id);
    if ((dir === 'up' && idx === 0) || (dir === 'down' && idx === data.fields.length - 1)) return;
    const arr = [...data.fields];
    const swap = dir === 'up' ? idx - 1 : idx + 1;
    [arr[idx], arr[swap]] = [arr[swap], arr[idx]];
    setData((d) => ({ ...d, fields: arr }));
  };

  const editingField = data.fields.find((f) => f.id === editingFieldId);

  const renderPreviewField = (field: FormField) => {
    const base = 'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 bg-gray-50';
    switch (field.type) {
      case 'textarea':
        return <textarea className={`${base} resize-none`} rows={3} placeholder={field.placeholder} readOnly />;
      case 'select':
        return (
          <select className={base}>
            <option value="">Select an option...</option>
            {(field.options || []).map((o) => <option key={o}>{o}</option>)}
          </select>
        );
      case 'checkbox':
        return (
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" className="rounded" readOnly />
            <span className="text-sm text-gray-600">{field.label}</span>
          </label>
        );
      default:
        return <input type={field.type} className={base} placeholder={field.placeholder} readOnly />;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-5xl mx-4 h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${data.color}18` }}>
              <i className={`${data.icon} text-base`} style={{ color: data.color }} />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-800">{form ? 'Edit Form' : 'Create New Form'}</h2>
              <p className="text-xs text-gray-400">{data.fields.length} field{data.fields.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer whitespace-nowrap">Cancel</button>
            <button
              onClick={() => onSave({ ...data, updatedAt: new Date().toISOString().split('T')[0] })}
              disabled={!data.name.trim()}
              className="px-4 py-2 text-sm text-white rounded-lg font-medium cursor-pointer whitespace-nowrap disabled:opacity-40"
              style={{ backgroundColor: data.color }}
            >
              {form ? 'Save Changes' : 'Create Form'}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 px-6 py-3 border-b border-gray-100">
          {(['fields', 'settings', 'preview'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setActiveTab(t)}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium cursor-pointer capitalize transition-colors ${
                activeTab === t ? 'text-gray-800 bg-gray-100' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t === 'fields' ? 'Form Fields' : t === 'settings' ? 'Settings' : 'Preview'}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-hidden flex">
          {/* Tab: Fields */}
          {activeTab === 'fields' && (
            <>
              {/* Left — field list */}
              <div className="w-80 border-r border-gray-100 flex flex-col">
                <div className="flex-1 overflow-y-auto p-4 space-y-2">
                  {data.fields.length === 0 ? (
                    <div className="text-center py-12 text-gray-400">
                      <i className="ri-layout-2-line text-3xl block mb-2 text-gray-200" />
                      <p className="text-sm">No fields yet</p>
                      <p className="text-xs mt-1">Add fields from the right panel</p>
                    </div>
                  ) : (
                    data.fields.map((field, idx) => (
                      <div
                        key={field.id}
                        onClick={() => setEditingFieldId(field.id)}
                        className={`flex items-center gap-2.5 p-3 rounded-xl border cursor-pointer transition-all ${
                          editingFieldId === field.id ? 'border-gray-400 bg-gray-50' : 'border-gray-100 hover:border-gray-200'
                        }`}
                      >
                        <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                          <i className={`${fieldTypes.find((f) => f.type === field.type)?.icon || 'ri-text'} text-sm text-gray-400`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-gray-700 truncate">{field.label}</p>
                          <p className="text-xs text-gray-400">{fieldTypes.find((f) => f.type === field.type)?.label}</p>
                        </div>
                        <div className="flex flex-col gap-0.5 flex-shrink-0">
                          <button onClick={(e) => { e.stopPropagation(); moveField(field.id, 'up'); }} disabled={idx === 0} className="text-gray-300 hover:text-gray-500 cursor-pointer disabled:opacity-30 w-4 h-4 flex items-center justify-center">
                            <i className="ri-arrow-up-s-line text-sm" />
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); moveField(field.id, 'down'); }} disabled={idx === data.fields.length - 1} className="text-gray-300 hover:text-gray-500 cursor-pointer disabled:opacity-30 w-4 h-4 flex items-center justify-center">
                            <i className="ri-arrow-down-s-line text-sm" />
                          </button>
                        </div>
                        {field.required && <span className="text-red-400 text-xs">*</span>}
                        <button onClick={(e) => { e.stopPropagation(); removeField(field.id); }} className="text-gray-300 hover:text-red-400 cursor-pointer w-5 h-5 flex items-center justify-center">
                          <i className="ri-delete-bin-line text-xs" />
                        </button>
                      </div>
                    ))
                  )}
                </div>

                {/* Field editor */}
                {editingField && (
                  <div className="border-t border-gray-100 p-4 bg-gray-50">
                    <p className="text-xs font-semibold text-gray-600 mb-3">Edit Field</p>
                    <div className="space-y-2">
                      <div>
                        <label className="text-xs text-gray-500 mb-1 block">Label</label>
                        <input
                          type="text"
                          value={editingField.label}
                          onChange={(e) => updateField(editingField.id, { label: e.target.value })}
                          className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-xs outline-none focus:border-gray-400"
                        />
                      </div>
                      {['text', 'email', 'phone', 'number', 'textarea'].includes(editingField.type) && (
                        <div>
                          <label className="text-xs text-gray-500 mb-1 block">Placeholder</label>
                          <input
                            type="text"
                            value={editingField.placeholder || ''}
                            onChange={(e) => updateField(editingField.id, { placeholder: e.target.value })}
                            className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-xs outline-none focus:border-gray-400"
                          />
                        </div>
                      )}
                      {editingField.type === 'select' && (
                        <div>
                          <label className="text-xs text-gray-500 mb-1 block">Options</label>
                          <div className="space-y-1 mb-2">
                            {(editingField.options || []).map((opt, i) => (
                              <div key={i} className="flex items-center gap-1">
                                <input
                                  type="text"
                                  value={opt}
                                  onChange={(e) => {
                                    const opts = [...(editingField.options || [])];
                                    opts[i] = e.target.value;
                                    updateField(editingField.id, { options: opts });
                                  }}
                                  className="flex-1 px-2 py-1 border border-gray-200 rounded text-xs"
                                />
                                <button onClick={() => updateField(editingField.id, { options: (editingField.options || []).filter((_, j) => j !== i) })} className="text-gray-300 hover:text-red-400 cursor-pointer">
                                  <i className="ri-close-line text-xs" />
                                </button>
                              </div>
                            ))}
                          </div>
                          <div className="flex gap-1">
                            <input
                              type="text"
                              placeholder="Add option..."
                              value={newOption}
                              onChange={(e) => setNewOption(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && newOption.trim()) {
                                  updateField(editingField.id, { options: [...(editingField.options || []), newOption.trim()] });
                                  setNewOption('');
                                }
                              }}
                              className="flex-1 px-2 py-1 border border-gray-200 rounded text-xs"
                            />
                            <button
                              onClick={() => { if (newOption.trim()) { updateField(editingField.id, { options: [...(editingField.options || []), newOption.trim()] }); setNewOption(''); } }}
                              className="px-2 text-xs text-white rounded cursor-pointer"
                              style={{ backgroundColor: data.color }}
                            >
                              Add
                            </button>
                          </div>
                        </div>
                      )}
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editingField.required}
                          onChange={(e) => updateField(editingField.id, { required: e.target.checked })}
                        />
                        <span className="text-xs text-gray-600">Required field</span>
                      </label>
                    </div>
                  </div>
                )}
              </div>

              {/* Right — add field panel */}
              <div className="flex-1 overflow-y-auto p-5">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Add Field</p>
                <div className="grid grid-cols-2 gap-2">
                  {fieldTypes.map((ft) => (
                    <button
                      key={ft.type}
                      onClick={() => addField(ft.type)}
                      className="flex items-center gap-2.5 p-3 border border-gray-100 rounded-xl hover:border-gray-300 hover:bg-gray-50 transition-all cursor-pointer text-left"
                    >
                      <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-gray-100">
                        <i className={`${ft.icon} text-sm text-gray-500`} />
                      </div>
                      <span className="text-sm text-gray-700 font-medium">{ft.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Tab: Settings */}
          {activeTab === 'settings' && (
            <div className="flex-1 overflow-y-auto p-6">
              <div className="max-w-lg space-y-5">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Form Name *</label>
                  <input type="text" value={data.name} onChange={(e) => setData((d) => ({ ...d, name: e.target.value }))} placeholder="e.g. Holiday Enquiry Form" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-gray-400" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Description</label>
                  <textarea value={data.description} onChange={(e) => setData((d) => ({ ...d, description: e.target.value }))} rows={3} placeholder="Brief description of this form..." className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-gray-400 resize-none" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Category</label>
                  <select value={data.category} onChange={(e) => setData((d) => ({ ...d, category: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-gray-400">
                    {categoryOptions.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-2">Icon</label>
                  <div className="flex items-center gap-2 flex-wrap">
                    {iconOptions.map((ic) => (
                      <button key={ic} onClick={() => setData((d) => ({ ...d, icon: ic }))} className={`w-9 h-9 rounded-xl flex items-center justify-center transition-all cursor-pointer border-2 ${data.icon === ic ? 'border-gray-400' : 'border-transparent bg-gray-100 hover:bg-gray-200'}`} style={data.icon === ic ? { backgroundColor: `${data.color}18`, borderColor: data.color } : {}}>
                        <i className={`${ic} text-base`} style={data.icon === ic ? { color: data.color } : { color: '#6b7280' }} />
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-2">Color</label>
                  <div className="flex items-center gap-2">
                    {colorOptions.map((c) => (
                      <button key={c} onClick={() => setData((d) => ({ ...d, color: c }))} className="w-7 h-7 rounded-full cursor-pointer border-2 transition-all" style={{ backgroundColor: c, borderColor: data.color === c ? '#374151' : 'transparent' }} />
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Embed Domain (optional)</label>
                  <input type="text" value={data.embedDomain || ''} onChange={(e) => setData((d) => ({ ...d, embedDomain: e.target.value }))} placeholder="e.g. yourwebsite.com" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-gray-400" />
                </div>
              </div>
            </div>
          )}

          {/* Tab: Preview */}
          {activeTab === 'preview' && (
            <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
              <div className="max-w-md mx-auto bg-white rounded-2xl border border-gray-200 overflow-hidden">
                {/* Form header */}
                <div className="p-6 border-b border-gray-100" style={{ background: `linear-gradient(135deg, ${data.color}08, ${data.color}18)` }}>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${data.color}20` }}>
                      <i className={`${data.icon} text-xl`} style={{ color: data.color }} />
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-gray-800">{data.name || 'Form Name'}</h3>
                      <p className="text-xs text-gray-500">Horizon Travel & Tourism</p>
                    </div>
                  </div>
                  {data.description && <p className="text-xs text-gray-500">{data.description}</p>}
                </div>

                <div className="p-6 space-y-4">
                  {data.fields.length === 0 ? (
                    <p className="text-center text-gray-400 text-sm py-8">Add fields to see preview</p>
                  ) : (
                    data.fields.map((field) => (
                      <div key={field.id}>
                        {field.type !== 'checkbox' && (
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {field.label}
                            {field.required && <span className="text-red-400 ml-1">*</span>}
                          </label>
                        )}
                        {renderPreviewField(field)}
                      </div>
                    ))
                  )}
                  {data.fields.length > 0 && (
                    <button className="w-full py-3 rounded-xl text-white text-sm font-semibold mt-2" style={{ backgroundColor: data.color }}>
                      Submit Enquiry
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
